import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { BullhornAuthData, importCandidatesFromBullhorn, fetchBullhornCandidateFiles, downloadBullhornCandidateFile, fetchBullhornCandidateNotes, fetchBullhornJobSubmissions, fetchBullhornCandidateJobSubmissions, fetchAllCandidatesViaQuery, fetchBullhornCandidateFull } from '../services/bullhornService';
import { parseBullhornRawToProfile, analyzeUploadedCV, generateEmbedding } from '../services/geminiService';
import { supabase_upsert_imported_candidates, supabase_sync_bullhorn_documents, supabase_sync_bullhorn_notes, supabase_sync_bullhorn_job_submissions, supabase } from '../services/supabaseService';
import { Loader2, Sparkles, Save, ChevronRight, ChevronLeft, X, Database, CheckCircle2, AlertCircle, RefreshCw, FileText } from 'lucide-react';

interface Props {
  user: User;
  bhAuth: BullhornAuthData;
  onClose: () => void;
  onRefreshAuth?: () => Promise<any>;
}

export const BullhornInteractiveMapper: React.FC<Props> = ({ user, bhAuth, onClose, onRefreshAuth }) => {
  const [analyzeStep, setAnalyzeStep] = useState<string>('');
  const [candidates, setCandidates] = useState<any[]>([]);
  const [allCandidates, setAllCandidates] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [viewStage, setViewStage] = useState<'table' | 'mapper'>('table');
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mappedProfile, setMappedProfile] = useState<any | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle'|'saving'|'success'|'error'|'conflict'>('idle');
  const [isAutoMode, setIsAutoMode] = useState(false);
  const [useFastSync, setUseFastSync] = useState(false);
  const [progressText, setProgressText] = useState<string>('');

  // Extra raw data fetch states for the left side
  const [currentCandidateFiles, setCurrentCandidateFiles] = useState<any[]>([]);
  const [currentCandidateNotes, setCurrentCandidateNotes] = useState<any[]>([]);
  const [currentCandidateSubmissions, setCurrentCandidateSubmissions] = useState<any[]>([]);
  const [isFetchingExtras, setIsFetchingExtras] = useState(false);

  useEffect(() => {
    const loadCandidates = async () => {
      setIsLoading(true);
      setCurrentIndex(0);
      setMappedProfile(null);
      setAllCandidates([]);
      setCandidates([]);
      
      try {
        setProgressText('Verzamelen van Bullhorn & Database kandidaten...');
        
        // 1. Fetch from Bullhorn
        const res = await importCandidatesFromBullhorn(bhAuth, 'isDeleted:0', 'id,firstName,lastName,email,phone,mobile,status,dateAdded', 200, true, setProgressText);
        const rawCandidates = Array.isArray(res) ? res : (res.data || []);

        // 2. Fetch from Database (All IDs, plus AI Parsed status)
        let allExistingRecords: any[] = [];
        let fetchStart = 0;
        while(true) {
            const { data } = await supabase.from('candidates').select('candidate_number, ai_parsed_data').not('candidate_number', 'is', null).order('id').range(fetchStart, fetchStart + 999);
            if (!data || data.length === 0) break;
            allExistingRecords = allExistingRecords.concat(data);
            if (data.length < 1000) break;
            fetchStart += 1000;
        }

        const existingMap = new Map();
        allExistingRecords.forEach((r: any) => {
            let isComplete = false;
            if (r.ai_parsed_data && Object.keys(r.ai_parsed_data).length > 0 && r.ai_parsed_data.importedFromMsg !== "Bullhorn Pipeline Sync (Minimal)") {
                isComplete = true;
            }
            existingMap.set(String(r.candidate_number), { isComplete });
        });

        // 3. Process Bullhorn List
        const unifiedList: any[] = [];
        rawCandidates.forEach((c: any) => {
            const strId = String(c.id);
            const exists = existingMap.has(strId);
            const isComplete = exists ? existingMap.get(strId).isComplete : false;
            
            unifiedList.push({
                ...c,
                dbStatus: exists ? (isComplete ? 'compleet' : 'hermap') : 'nieuw',
                isRemapMode: exists
            });
        });

        // 4. Fetch known incomplete candidates from DB that didn't show up in the top Bullhorn fetch
        let q = supabase.from('candidates')
             .select('id, candidate_number, name, email, phone, ai_parsed_data, status')
             .or('ai_parsed_data.is.null,ai_parsed_data.eq.{},ai_parsed_data->>importedFromMsg.eq."Bullhorn Pipeline Sync (Minimal)"')
             .not('candidate_number', 'is', null);
          
        if (user.clientId) q = q.eq('client_id', user.clientId);
        const { data: incompleteData } = await q.limit(100);
        
        if (incompleteData) {
            incompleteData.forEach((inc: any) => {
                const strId = String(inc.candidate_number);
                if (!unifiedList.find(c => String(c.id) === strId)) {
                    unifiedList.push({
                        id: strId,
                        firstName: inc.name?.split(' ')[0] || 'Onbekend',
                        lastName: inc.name?.split(' ').slice(1).join(' ') || '',
                        email: inc.email || '',
                        phone: inc.phone || '',
                        status: inc.status || 'Active',
                        dateAdded: new Date().getTime(),
                        dbStatus: 'hermap',
                        isRemapMode: true
                    });
                }
            });
        }

        setAllCandidates(unifiedList);
        
        // Auto-select 'nieuw' and 'hermap'
        const initialSelection = new Set<string>();
        unifiedList.forEach(c => {
            if (c.dbStatus === 'nieuw' || c.dbStatus === 'hermap') {
                initialSelection.add(String(c.id));
            }
        });
        setSelectedIds(initialSelection);
        
      } catch (err: any) {
        console.error("Failed to load candidates:", err);
        if (err.message && err.message.includes('401') && onRefreshAuth) {
            console.log("Token expired during initial load. Replenishing...");
            await onRefreshAuth();
        }
      } finally {
        setIsLoading(false);
      }
    };
    loadCandidates();
  }, []);

  // State Machine for Auto-Loop
  useEffect(() => {
     if (!isAutoMode) return;
     if (isFetchingExtras) return; // Wait for initial raw data fetch

     if (!mappedProfile && !isAnalyzing && saveStatus === 'idle') {
         handleAnalyze();
         return;
     }

     if (mappedProfile && saveStatus === 'idle' && !isSaving) {
         handleSaveAndSync();
         return;
     }
  }, [isAutoMode, isFetchingExtras, mappedProfile, isAnalyzing, saveStatus, isSaving]);

  const currentCandidate = candidates[currentIndex];

  useEffect(() => {
     const fetchExtras = async () => {
        if (!currentCandidate) return;
        setIsFetchingExtras(true);
        try {
            const files = await fetchBullhornCandidateFiles(bhAuth, currentCandidate.id);
            setCurrentCandidateFiles(files || []);
            
            const notes = await fetchBullhornCandidateNotes(bhAuth, currentCandidate.id);
            setCurrentCandidateNotes(notes || []);

            // Deep load Candidate explicitly to get workHistories which can't be fetched via search
            const fullProfile = await fetchBullhornCandidateFull(bhAuth, currentCandidate.id);
            setCandidates(prev => prev.map((c, i) => i === currentIndex ? { ...c, ...fullProfile } : c));

            const submissions = await fetchBullhornCandidateJobSubmissions(bhAuth, currentCandidate.id);
            setCurrentCandidateSubmissions(submissions || []);
        } catch(e) {
            console.error("Failed fetching extra candidate info:", e);
        } finally {
            setIsFetchingExtras(false);
        }
     };
     fetchExtras();
  }, [currentIndex, candidates.length]);

  const handleNext = () => {
    if (currentIndex < candidates.length - 1) {
       setCurrentIndex(currentIndex + 1);
       setMappedProfile(null);
       setSaveStatus('idle');
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
       setCurrentIndex(currentIndex - 1);
       setMappedProfile(null);
       setSaveStatus('idle');
    }
  };

  const handleAnalyze = async () => {
    if (!currentCandidate) return;
    setIsAnalyzing(true);
    setSaveStatus('idle');
    setAnalyzeStep('Zoeken naar CV bestand...');
    try {
      let parsed;
      let candData = currentCandidate;
      // In Fast mode, fetchExtras might not have finished yet, so ensure we have workHistories
      if (!candData.workHistories) {
          try {
              const fullProfile = await fetchBullhornCandidateFull(bhAuth, candData.id);
              candData = { ...candData, ...fullProfile };
          } catch(e) {}
      }

      if (useFastSync) {
          setAnalyzeStep('Snelle Directe Data Mapping...');
          
          let cleanSummary = candData.description || '';
          if (cleanSummary) {
              cleanSummary = cleanSummary.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
                                         .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
                                         .replace(/<br\s*\/?>/gi, '\n')
                                         .replace(/<\/p>/gi, '\n\n')
                                         .replace(/<[^>]+>/g, '')
                                         .trim();
          }

          parsed = {
              firstName: candData.firstName || '',
              lastName: candData.lastName || '',
              email: candData.email || candData.email2 || '',
              phone: candData.phone || candData.mobile || '',
              summary: cleanSummary, // Retain full original CV content, no truncation
              skills: typeof candData.skillSet === 'string' ? candData.skillSet.split(',').map((s:any)=>s.trim()) : (candData.skillSet || []),
              tags: typeof candData.skillSet === 'string' ? candData.skillSet.split(',').map((s:any)=>s.trim()) : (candData.skillSet || []),
              timeline: [], // Will be mapped securely via the smart merge below from pure Bullhorn records
              educations: [], // Will be mapped securely via the smart merge below from pure Bullhorn records
              courses: [],
              hobbies: [],
              currentRole: candData.occupation || (candData.workHistories?.data?.[0]?.title) || ''
          };
      } else {
          // STRICT SYNCHRONOUS FETCH: Do not rely on 'currentCandidateFiles' state to prevent Race Conditions 
          // where Auto-Mode reads the previous candidate's CV!
          const files = await fetchBullhornCandidateFiles(bhAuth, candData.id) || [];
          setCurrentCandidateFiles(files);
          const cvFileMeta = files.find((f:any) => f.name?.toLowerCase().includes('.pdf') || f.name?.toLowerCase().includes('.doc'));
    
          if (cvFileMeta) {
              setAnalyzeStep(`Downloaden ${cvFileMeta.name}...`);
              const rawFile = await downloadBullhornCandidateFile(bhAuth, candData.id, cvFileMeta.id);
              const file = rawFile.File ? rawFile.File : rawFile;
              
              if (file && file.fileContent) {
                  setAnalyzeStep('CV Analyseren via Froukje...');
                  parsed = await analyzeUploadedCV({ base64: file.fileContent, mimeType: file.contentType || 'application/pdf' }, undefined, candData.description?.replace(/<[^>]+>/g, ''));
              } else {
                  setAnalyzeStep('Bullhorn Data Analyseren...');
                  parsed = await parseBullhornRawToProfile(candData);
              }
          } else {
              setAnalyzeStep('Bullhorn Data Analyseren...');
              parsed = await parseBullhornRawToProfile(candData);
          }
      }

      // Map raw structured history and educations from Bullhorn to override AI if it exists
      // ONLY override if we used parseBullhornRawToProfile, since physical CV parses are usually BETTER.
      let combinedTags = parsed.tags || parsed.skills || [];
      if (candData.skillSet) {
          const bhTags = Array.isArray(candData.skillSet) ? candData.skillSet : (typeof candData.skillSet === 'string' ? candData.skillSet.split(',').map((s:string) => s.trim()) : []);
          combinedTags = Array.from(new Set([...combinedTags, ...bhTags])).filter(Boolean);
      }
      parsed.skills = combinedTags;
      parsed.tags = combinedTags;

      let mappedTimeline = parsed.timeline?.map((t:any) => ({
          title: t.title || t.role || 'Onbekend',
          company: t.company || 'Onbekend Bedrijf',
          period: t.period || t.year || 'jaartal onbekend',
          description: t.description || ''
      })) || [];
      
      let mappedEducations = parsed.education?.map((ed:any) => ({
          school: ed.institution || ed.school || '',
          degree: ed.degree || '',
          period: ed.year || ed.period || ''
      })) || [];

      // Smart Merge Bullhorn Work Histories into AI Timeline to prevent data loss
      let bhTimeline = (candData.workHistories?.data || []).map((wh: any) => ({
          title: wh.title || 'Onbekend',
          company: wh.companyName || 'Onbekend Bedrijf',
          period: `${wh.startDate ? new Date(wh.startDate).getFullYear() : 'Onbekend'} - ${wh.endDate ? new Date(wh.endDate).getFullYear() : 'Heden'}`,
          description: wh.comments ? wh.comments.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '').replace(/<[^>]+>/g, '').trim() : ''
      }));
      
      if (!mappedTimeline) mappedTimeline = [];
      bhTimeline.forEach((bh: any) => {
          const existsInAI = mappedTimeline.some((t:any) => 
               t.company && bh.company && (t.company.toLowerCase().includes(bh.company.toLowerCase()) || bh.company.toLowerCase().includes(t.company.toLowerCase()))
          );
          if (!existsInAI) {
              mappedTimeline.push({ ...bh });
          }
      });

      // Smart Merge Bullhorn Educations into AI Educations
      let bhEducations = (candData.educations?.data || []).map((ed: any) => ({
          institution: ed.school || 'Onbekende Instelling',
          degree: ed.degree || 'Onbekende Diploma',
          period: `${ed.startDate ? new Date(ed.startDate).getFullYear() : 'Onbekend'} - ${ed.endDate ? new Date(ed.endDate).getFullYear() : 'Heden'}`
      }));
      
      bhEducations.forEach((bh: any) => {
          const existsInAI = mappedEducations.some((t:any) => 
               t.school && bh.institution && (t.school.toLowerCase().includes(bh.institution.toLowerCase()) || bh.institution.toLowerCase().includes(t.school.toLowerCase()))
          );
          if (!existsInAI) {
              mappedEducations.push({ school: bh.institution, degree: bh.degree, period: bh.period });
          }
      });

      setMappedProfile({
        ...parsed,
        firstName: currentCandidate.firstName || parsed.firstName,
        lastName: currentCandidate.lastName || parsed.lastName,
        name: `${currentCandidate.firstName || ''} ${currentCandidate.lastName || ''}`.trim() || parsed.name,
        email: currentCandidate.email || parsed.email,
        phone: currentCandidate.phone || currentCandidate.mobile || parsed.phone,
        timeline: mappedTimeline,
        educations: mappedEducations,
        courses: parsed.courses || [],
        hobbies: parsed.hobbies || [],
        bullhorn_id: currentCandidate.id,
        source: 'bullhorn',
        cv_text_verbatim: currentCandidate.description || '' 
      });
    } catch (err: any) {
      console.error("Analysis Failed:", err);
      const isRateLimit = err.status === 429 || err.message?.includes('429') || err.message?.includes('exhausted') || err.message?.includes('quota');
      
      if (isAutoMode) {
          if (isRateLimit) {
              setAnalyzeStep('AI Rate Limit geraakt. 30 sec pauzeren...');
              await new Promise(r => setTimeout(r, 30000));
              handleAnalyze();
              return; // Skip the finally block since we're retrying
          } else {
              // Not a rate limit, just a bad CV / unparsable data. 
              // Do NOT retry infinitely. Skip to the next candidate!
              console.warn("Unrecoverable AI error during AutoMode, skipping candidate...", err.message);
              // Wait 2 seconds so the user can see it failed before jumping
              setAnalyzeStep('CV Onleesbaar. Kandidaat overslaan...');
              await new Promise(r => setTimeout(r, 2000));
              handleNext();
              return;
          }
      } else {
          alert("AI Analyseren mislukt: " + (err.message || String(err)));
      }
    } finally {
      setIsAnalyzing(false);
      setAnalyzeStep('');
    }
  };

  const handleSaveAndSync = async () => {
    if (!currentCandidate) return;
    setIsSaving(true);
    setSaveStatus('saving');

    try {
      // Create an array with a structure similar to what `importCandidatesFromBullhorn` gives, 
      // but injecting the AI parsed data if we have it, else we fallback to standard logic.
      // Actually, standard logic in `supabase_upsert_imported_candidates` only saves raw data if ai_parsed_data doesn't exist.
      // So let's construct the payload correctly.
      const payload = {
         ...currentCandidate,
         _ai_overrides: mappedProfile // We will intercept this in the backend, or handle it manually here to be safe
      };
      
      // Find existing Candidate STRICTLY by candidate_number to prevent merging distinct identities
      let emailToUse = currentCandidate.email || currentCandidate.email2 || `${currentCandidate.id}@no-email.bullhorn`;
      const candidateNum = parseInt(currentCandidate.id, 10);
      
      let { data: existingCandidate } = await supabase.from('candidates').select('id, email').eq('candidate_number', candidateNum).maybeSingle();
      
      // Before inserting or updating, we MUST guarantee this email is completely unique in the DB
      // because otherwise Postgres will throw a 23505 Duplicate Key Constraint crash.
      const { data: emailConflict } = await supabase.from('candidates').select('id, candidate_number').ilike('email', emailToUse).maybeSingle();
      
      // If another DIFFERENT candidate is using this exact email, append our Bullhorn ID to make it unique!
      if (emailConflict && emailConflict.candidate_number !== candidateNum) {
           console.warn(`Email ${emailToUse} is already in use by DB candidate ${emailConflict.id}. Mutating to guarantee insertion!`);
           emailToUse = `${currentCandidate.id}-${emailToUse}`;
      }

      let candId;
      // Insert / Update strictly onto candidates table
      const candPayload: any = {
          name: `${currentCandidate.firstName || ''} ${currentCandidate.lastName || ''}`.trim() || 'Unknown Candidate',
          phone: currentCandidate.phone || currentCandidate.mobile || null,
          candidate_number: candidateNum,
          skill_set: mappedProfile?.skills || (Array.isArray(currentCandidate.skillSet) ? currentCandidate.skillSet : (typeof currentCandidate.skillSet === 'string' ? currentCandidate.skillSet.split(',') : [])),
          original_cv_text: mappedProfile?.summary || currentCandidate.description || '',
          source: 'bullhorn',
          address_city: currentCandidate.address?.city || null,
          address_zip: currentCandidate.address?.zip || null,
          address_country: currentCandidate.address?.countryName || null,
          client_id: user.clientId, // Set ownership natively
          status: 'Lead',
          ai_parsed_data: mappedProfile || null
      };

      try {
           let emText = `Name: ${candPayload.name}\n`;
           if (mappedProfile?.summary) emText += `Summary: ${mappedProfile.summary}\n`;
           if (currentCandidate.description) emText += `Original CV Context: ${currentCandidate.description.substring(0, 1000)}\n`;
           if (candPayload.skill_set && candPayload.skill_set.length > 0) emText += `Skills: ${candPayload.skill_set.join(', ')}\n`;
           const embed = await generateEmbedding(emText.substring(0, 9500));
           if (embed) {
               candPayload.embedding = embed;
               console.log("Vector Sync Generated directly in Mapper.");
           }
      } catch (embErr) {
           console.error("Failed real-time embedding in Mapper:", embErr);
      }

      if (existingCandidate) {
          candId = existingCandidate.id;
          const { error: candError } = await supabase.from('candidates').update(candPayload).eq('id', candId);
          if (candError) throw candError;
      } else {
          const { data: newCand, error: insError } = await supabase.from('candidates').insert({
              ...candPayload,
              email: emailToUse,
          }).select('id').single();
          if (insError) throw insError;
          candId = newCand.id;
      }

      // Deep Sync Notes directly to Candidate
      const notes = await fetchBullhornCandidateNotes(bhAuth, currentCandidate.id);
      if (notes && notes.length > 0 && candId) {
          await supabase_sync_bullhorn_notes(user.clientId as string, candId, user.id, notes, candId);
      }

      // Deep Sync Files
      const filesMetadata = await fetchBullhornCandidateFiles(bhAuth, currentCandidate.id);
      if (filesMetadata && filesMetadata.length > 0) {
          const filesWithContent = [];
          for (const fm of filesMetadata) {
              try {
                  const fileContent = await downloadBullhornCandidateFile(bhAuth, currentCandidate.id, fm.id);
                  filesWithContent.push(fileContent);
              } catch(e) { }
          }
          if (filesWithContent.length > 0) {
              await supabase_sync_bullhorn_documents(user.clientId as string, candId, filesWithContent);
          }
      }

      // Deep Sync Job Submissions (Vacature Pipelines)
      const submissions = await fetchBullhornCandidateJobSubmissions(bhAuth, currentCandidate.id);
      if (submissions && submissions.length > 0) {
          const jobOrderIds = submissions.map((s: any) => String(s.jobOrder?.id));
          if (jobOrderIds.length > 0) {
             const { data: localVacs } = await supabase.from('vacancies').select('id, bullhorn_id').in('bullhorn_id', jobOrderIds);
             if (localVacs && localVacs.length > 0) {
                 for (const localVac of localVacs) {
                     const vacSubs = submissions.filter((s:any) => String(s.jobOrder?.id) === String(localVac.bullhorn_id));
                     // Reuse the job submission syncer which handles the reverse mapping
                     await supabase_sync_bullhorn_job_submissions(user.clientId as string, localVac.id, vacSubs);
                 }
             }
          }
      }

      setSaveStatus('success');
      
      // Auto move
      if (isAutoMode) {
          handleNext();
      } else {
          setTimeout(() => { handleNext(); }, 1500);
      }

    } catch (err: any) {
    console.error("Save failed:", err);
    if (err.message && err.message.includes('401') && onRefreshAuth) {
        console.log("Token expired during sync. Automatically refreshing...");
        const newAuth = await onRefreshAuth();
        if (newAuth) {
            setSaveStatus('idle'); // The auto-loop will quickly try again
            setIsSaving(false);
            return;
        }
    }
    
    if (err.code === '23505' || err.message?.toLowerCase().includes('conflict') || err.message?.toLowerCase().includes('duplicate key')) {
        if (isAutoMode) {
            console.error("Conflict during Auto Mode. Skipping candidate...", err);
            setSaveStatus('conflict');
            await new Promise(r => setTimeout(r, 2000));
            handleNext();
        } else {
            setSaveStatus('conflict');
        }
        return;
    }

       if (isAutoMode) {
           console.error("Vector Sync failed during Auto Mode. Skipping candidate...", err);
           setSaveStatus('error');
           await new Promise(r => setTimeout(r, 2000));
           handleNext();
           return;
       } else {
           setSaveStatus('error');
           alert("Error: " + err.message);
       }
    } finally {
       setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900 z-[60] flex flex-col font-sans">
      {/* Header */}
      <div className="bg-slate-950 border-b border-slate-800 px-6 py-4 flex items-center justify-between shrink-0 shadow-lg text-white">
        <h1 className="text-xl font-black tracking-tight flex items-center gap-3">
            <Database size={24} className="text-[#e51b23]" />
            Froukje Interactive Mapper <span className="hidden lg:inline text-sm font-bold text-slate-500 uppercase tracking-widest ml-4">Bullhorn Sync Phase 2</span>
        </h1>
        
        <div className="flex items-center gap-4">

            <button 
              onClick={onClose}
              className="w-10 h-10 bg-slate-800 text-slate-400 rounded-full flex items-center justify-center hover:bg-[#e51b23] hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex-1 flex flex-col items-center justify-center text-slate-500 gap-4">
           <Loader2 size={40} className="animate-spin text-[#e51b23]" />
           <p className="text-sm font-bold uppercase tracking-widest animate-pulse">{progressText || 'Loading Candidates from Bullhorn...'}</p>
        </div>
      ) : viewStage === 'table' ? (
        <div className="flex-1 flex flex-col overflow-hidden bg-slate-50">
           {/* Table View Header */}
           <div className="bg-white px-6 py-4 border-b border-slate-200 flex items-center justify-between shadow-sm z-10 shrink-0">
               <div>
                   <h2 className="text-lg font-black text-slate-900">Kies Kandidaten om te Synchroniseren</h2>
                   <p className="text-sm text-slate-500">Gevonden in Bullhorn & Database: {allCandidates.length}</p>
               </div>
               <button 
                  onClick={() => {
                      setCandidates(allCandidates.filter(c => selectedIds.has(String(c.id))));
                      setViewStage('mapper');
                  }}
                  disabled={selectedIds.size === 0}
                  className="bg-[#e51b23] hover:bg-[#c9181e] disabled:opacity-50 text-white px-6 py-2 rounded-xl font-bold uppercase tracking-widest text-sm shadow-md transition-all flex items-center gap-2"
               >
                   Start Froukje Mapping ({selectedIds.size}) <ChevronRight size={16} />
               </button>
           </div>
           
           {/* Table Content */}
           <div className="flex-1 overflow-y-auto p-6">
               <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden min-h-full">
                   <table className="w-full text-left border-collapse">
                       <thead>
                           <tr className="bg-slate-50 border-b border-slate-200 text-xs font-black uppercase tracking-widest text-slate-500">
                               <th className="p-4 w-12 text-center">
                                   <input type="checkbox" 
                                      checked={selectedIds.size === allCandidates.length && allCandidates.length > 0} 
                                      onChange={(e) => {
                                          if (e.target.checked) setSelectedIds(new Set(allCandidates.map(c => String(c.id))));
                                          else setSelectedIds(new Set());
                                      }}
                                      className="w-4 h-4 rounded border-slate-300 text-[#e51b23] focus:ring-[#e51b23]"
                                   />
                               </th>
                               <th className="p-4">Kandidaat</th>
                               <th className="p-4">Contact</th>
                               <th className="p-4">Status & Datum</th>
                               <th className="p-4">Database Check</th>
                           </tr>
                       </thead>
                       <tbody className="divide-y divide-slate-100">
                           {allCandidates.map((c) => (
                               <tr key={c.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => {
                                   const newSet = new Set(selectedIds);
                                   if (newSet.has(String(c.id))) newSet.delete(String(c.id));
                                   else newSet.add(String(c.id));
                                   setSelectedIds(newSet);
                               }}>
                                   <td className="p-4 text-center">
                                       <input type="checkbox" 
                                          checked={selectedIds.has(String(c.id))} 
                                          onChange={() => {}} 
                                          className="w-4 h-4 rounded border-slate-300 text-[#e51b23] focus:ring-[#e51b23] cursor-pointer"
                                          onClick={(e) => e.stopPropagation()}
                                       />
                                   </td>
                                   <td className="p-4">
                                       <div className="flex items-center gap-3">
                                           <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-black shrink-0">
                                               {c.firstName?.charAt(0) || '?'}{c.lastName?.charAt(0) || '?'}
                                           </div>
                                           <div>
                                               <div className="text-sm font-black text-slate-900">{c.firstName} {c.lastName}</div>
                                               <div className="text-xs text-slate-500">ID: {c.id}</div>
                                           </div>
                                       </div>
                                   </td>
                                   <td className="p-4">
                                       <div className="text-xs text-slate-600 font-medium">{c.email || 'Geen email'}</div>
                                       <div className="text-xs text-slate-500">{c.phone || c.mobile || 'Geen telefoon'}</div>
                                   </td>
                                   <td className="p-4">
                                       <span className="inline-block px-2 py-1 bg-slate-100 text-slate-600 rounded text-[10px] font-bold uppercase tracking-widest">{c.status || 'Active'}</span>
                                       <div className="text-[11px] text-slate-400 mt-1">{c.dateAdded ? new Date(c.dateAdded).toLocaleDateString() : 'Onbekend'}</div>
                                   </td>
                                   <td className="p-4">
                                       {c.dbStatus === 'nieuw' && <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-black uppercase tracking-widest border border-blue-200"><Sparkles size={12}/> Nieuw in Bullhorn</span>}
                                       {c.dbStatus === 'hermap' && <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 rounded-lg text-xs font-black uppercase tracking-widest border border-orange-200"><RefreshCw size={12}/> Her-Map (Onvolledig in DB)</span>}
                                       {c.dbStatus === 'compleet' && <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-black uppercase tracking-widest border border-emerald-200"><CheckCircle2 size={12}/> Compleet (Al in DB)</span>}
                                   </td>
                               </tr>
                           ))}
                           {allCandidates.length === 0 && (
                               <tr>
                                   <td colSpan={5} className="p-8 text-center text-slate-500 font-medium">Geen kandidaten gevonden.</td>
                               </tr>
                           )}
                       </tbody>
                   </table>
               </div>
           </div>
        </div>
      ) : candidates.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-slate-500 gap-4">
           Geen kandidaten geselecteerd om te mappen.
           <button onClick={() => setViewStage('table')} className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors">
              Terug naar Tabel
           </button>
        </div>
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden">
           {/* Pagination Bar */}
           <div className="bg-slate-800 px-6 py-3 flex items-center justify-between border-b border-slate-700 shadow-md">
               <button onClick={handlePrev} disabled={currentIndex === 0 || isSaving} className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl text-xs font-bold uppercase disabled:opacity-50 transition-colors">
                  <ChevronLeft size={16} /> Vorige Kandidaat
               </button>
               
               <div className="flex items-center gap-4 text-xs font-black text-slate-400 tracking-widest uppercase">
                  <button onClick={() => { setIsAutoMode(false); setViewStage('table'); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 border border-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors mr-2">
                     <Database size={12} /> <span className="text-[10px]">Tabel</span>
                  </button>
                  <span>Kandidaat {currentIndex + 1} van {candidates.length}</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-600"></span>
                  <span className="text-emerald-400">Bullhorn ID: #{currentCandidate.id}</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-600"></span>
                  {isAutoMode ? (
                      <button onClick={() => setIsAutoMode(false)} className="px-4 py-1.5 bg-[#e51b23] text-white font-bold rounded-lg shadow uppercase tracking-widest flex items-center gap-2 animate-pulse transition-all hover:bg-red-700">
                          Stop Auto-Map
                      </button>
                  ) : (
                      <button onClick={() => setIsAutoMode(true)} className="px-4 py-1.5 bg-slate-900 border border-slate-700 text-white font-bold rounded-lg hover:bg-slate-700 transition uppercase tracking-widest flex items-center gap-2">
                          <Sparkles size={14} /> Start Auto-Mappen
                      </button>
                  )}
               </div>
               
               <button onClick={handleNext} disabled={currentIndex === candidates.length - 1 || isSaving} className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl text-xs font-bold uppercase disabled:opacity-50 transition-colors">
                  Volgende Kandidaat <ChevronRight size={16} />
               </button>
           </div>

           {/* Split Screen Area */}
           <div className="flex-1 flex flex-col lg:flex-row overflow-hidden bg-slate-100">
               {/* LEFT SIDE: RAW BULLHORN */}
               <div className="flex-1 flex flex-col border-r border-slate-300 relative bg-white">
                  <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between shrink-0">
                      <div className="flex items-center gap-2 text-slate-700 font-black text-sm uppercase tracking-widest">
                          <Database size={16} className="text-indigo-500" /> RAW Bullhorn Data
                      </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                      <div className="space-y-1">
                          <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">Naam & Contact</div>
                          <div className="text-3xl font-black text-slate-900 leading-none">{currentCandidate.firstName} {currentCandidate.lastName}</div>
                          <div className="text-sm font-medium text-slate-600 flex flex-col gap-1 pt-1">
                             <span>📧 {currentCandidate.email || currentCandidate.email2 || 'Geen email'}</span>
                             <span>📱 {currentCandidate.phone || currentCandidate.mobile || 'Geen telefoon'}</span>
                             {currentCandidate.address && (
                                <span className="text-xs text-slate-500 mt-1">
                                   📍 {currentCandidate.address.address1} {currentCandidate.address.address2 ? currentCandidate.address.address2 : ''}, {currentCandidate.address.zip} {currentCandidate.address.city}
                                </span>
                             )}
                          </div>
                      </div>

                      {currentCandidate.skillSet && (
                         <div className="space-y-2">
                             <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">Skillset (Raw String)</div>
                             <div className="bg-slate-100 p-3 rounded-lg text-sm text-slate-700 font-mono italic">{currentCandidate.skillSet}</div>
                         </div>
                      )}

                      {currentCandidate.workHistories && currentCandidate.workHistories.data && currentCandidate.workHistories.data.length > 0 && (
                         <div className="space-y-2">
                             <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">Ruwe Werkervaringen ({currentCandidate.workHistories.data.length})</div>
                             <div className="space-y-3">
                                {currentCandidate.workHistories.data.map((wh: any, i: number) => (
                                   <div key={i} className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
                                      <div className="text-xs font-bold text-slate-900">{wh.title || 'Unknown Title'}</div>
                                      <div className="text-[11px] text-slate-500">{wh.companyName || 'Unknown Company'} | {wh.startDate ? new Date(wh.startDate).getFullYear() : '?'} - {wh.endDate ? new Date(wh.endDate).getFullYear() : 'Heden'}</div>
                                      {wh.comments && <div className="text-xs text-slate-600 mt-2 line-clamp-3">{wh.comments.replace(/<[^>]+>/g, '')}</div>}
                                   </div>
                                ))}
                             </div>
                         </div>
                      )}

                      {currentCandidate.educations && currentCandidate.educations.data && currentCandidate.educations.data.length > 0 && (
                         <div className="space-y-2">
                             <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">Opleidingen ({currentCandidate.educations.data.length})</div>
                             <div className="space-y-3">
                                {currentCandidate.educations.data.map((ed: any, i: number) => (
                                   <div key={i} className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
                                      <div className="text-xs font-bold text-slate-900">{ed.degree || 'Onbekende Diploma'}</div>
                                      <div className="text-[11px] text-slate-500">{ed.school || 'Onbekende School'} | {ed.startDate ? new Date(ed.startDate).getFullYear() : '?'} - {ed.endDate ? new Date(ed.endDate).getFullYear() : '?'}</div>
                                   </div>
                                ))}
                             </div>
                         </div>
                      )}

                      {/* Display Courses/Hobbies if mapped to some custom properties, mostly just prompt output if standard not available */}

                      {currentCandidate.description && (
                         <div className="space-y-2">
                             <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">Raw Description / Profiel Dump</div>
                             <div className="bg-yellow-50/50 border border-yellow-100 p-3 rounded-lg text-[11px] text-slate-700 font-mono overflow-hidden max-h-40 overflow-y-auto custom-scrollbar">
                                 {currentCandidate.description.replace(/<[^>]+>/g, '')}
                             </div>
                         </div>
                      )}

                      {/* EXTRA FETCHED ITEMS */}
                      {isFetchingExtras ? (
                         <div className="py-4 flex justify-center"><Loader2 size={16} className="animate-spin text-slate-400" /></div>
                      ) : (
                         <>
                            {currentCandidateFiles.length > 0 && (
                               <div className="space-y-2">
                                  <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">Gevonden Documenten ({currentCandidateFiles.length})</div>
                                  <div className="flex flex-wrap gap-2">
                                     {currentCandidateFiles.map((f:any, i:number) => (
                                        <div key={i} className="bg-slate-100 px-3 py-2 rounded-lg text-xs font-medium text-slate-700 border border-slate-200 flex items-center gap-2">
                                           <FileText size={14} className="text-slate-400" /> {f.name || f.fileType || 'Document'}
                                        </div>
                                     ))}
                                  </div>
                               </div>
                            )}

                            {currentCandidateNotes.length > 0 && (
                               <div className="space-y-2">
                                  <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">Gevonden Notities ({currentCandidateNotes.length})</div>
                                  <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar pr-2">
                                     {currentCandidateNotes.map((n:any, i:number) => (
                                        <div key={i} className="bg-indigo-50/50 border border-indigo-100 p-3 rounded-lg">
                                           <div className="text-[10px] text-indigo-400 mb-1">{new Date(n.dateAdded).toLocaleDateString()} {n.action ? `- ${n.action}` : ''}</div>
                                           <div className="text-xs text-slate-700 line-clamp-3">{n.comments ? n.comments.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '').replace(/<[^>]+>/g, '').replace(/&[a-zA-Z]+;/g, ' ').trim() : ''}</div>
                                        </div>
                                     ))}
                                  </div>
                               </div>
                            )}

                            {currentCandidateSubmissions.length > 0 && (
                               <div className="space-y-2">
                                  <div className="text-[10px] uppercase font-black tracking-widest text-slate-400">Gekoppelde Vacatures ({currentCandidateSubmissions.length})</div>
                                  <div className="space-y-2">
                                     {currentCandidateSubmissions.map((s:any, i:number) => (
                                        <div key={i} className="bg-emerald-50/50 border border-emerald-100 p-3 rounded-lg flex justify-between items-center">
                                           <div>
                                              <div className="text-xs font-bold text-slate-900">{s.jobOrder?.title || `JobOrder #${s.jobOrder?.id}`}</div>
                                              <div className="text-[10px] text-slate-500 mt-1">Status: {s.status} | Toegevoegd op: {new Date(s.dateAdded).toLocaleDateString()}</div>
                                           </div>
                                        </div>
                                     ))}
                                  </div>
                               </div>
                            )}
                         </>
                      )}
                  </div>
               </div>

               {/* MIDDLE: THE BUTTON */}
               <div className="lg:w-28 bg-slate-200 border-x border-slate-300 flex flex-row lg:flex-col items-center justify-center p-4 relative z-10 text-center gap-4">
                   <button 
                     onClick={handleAnalyze} 
                     disabled={isAnalyzing || isSaving}
                     className="bg-[#e51b23] hover:bg-black text-white w-14 h-14 rounded-2xl shadow-xl flex items-center justify-center transform transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:hover:scale-100 disabled:bg-slate-400 group flex-shrink-0"
                     title="Analyseer en Formatteer met Froukje AI"
                   >
                      {isAnalyzing ? <Loader2 size={24} className="animate-spin" /> : <Sparkles size={24} className="group-hover:animate-pulse" />}
                   </button>
                   {isAnalyzing ? (
                       <div className="text-[9px] font-black tracking-widest text-[#e51b23] uppercase animate-pulse">
                           {analyzeStep || 'Analyseren...'}
                       </div>
                   ) : (
                       <div className="hidden lg:block text-[#e51b23] font-black text-[10px] tracking-widest uppercase rotate-180" style={{ writingMode: 'vertical-rl' }}>
                           Froukje Bridge
                       </div>
                   )}
               </div>

               {/* RIGHT SIDE: MAPPED SUPABASE DATA */}
               <div className="flex-1 flex flex-col relative bg-slate-50">
                  <div className="p-4 bg-white border-b border-slate-200 flex items-center justify-between shrink-0">
                      <div className="flex items-center gap-2 text-emerald-600 font-black text-sm uppercase tracking-widest">
                          <CheckCircle2 size={16} /> Werftools Database Profiel
                      </div>
                      
                      {mappedProfile && (
                         <button 
                            onClick={handleSaveAndSync} 
                            disabled={isSaving || saveStatus === 'success'}
                            className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 shadow-md transition-all ${
                               saveStatus === 'success' ? 'bg-emerald-500 text-white' : 
                               saveStatus === 'conflict' ? 'bg-orange-500 text-white' : 
                               saveStatus === 'error' ? 'bg-red-500 text-white' : 
                               'bg-emerald-600 hover:bg-emerald-700 text-white'
                            }`}
                         >
                            {isSaving ? <Loader2 size={16} className="animate-spin" /> : saveStatus === 'success' ? <CheckCircle2 size={16} /> : saveStatus === 'conflict' ? <AlertCircle size={16} /> : <Save size={16} />}
                            {isSaving ? 'Opslaan & Syncen...' : saveStatus === 'success' ? 'Voltooid' : saveStatus === 'conflict' ? 'Conflict' : 'Opslaan & Documenten Syncen'}
                         </button>
                      )}
                  </div>
                  <div className="flex-1 overflow-y-auto p-6 space-y-6">
                      {saveStatus === 'conflict' ? (
                         <div className="h-full flex flex-col items-center justify-center text-center opacity-90 animate-fadeIn">
                             <AlertCircle size={64} className="text-orange-500 mb-4 animate-bounce" />
                             <h3 className="text-2xl font-black text-slate-800">Gegeven Conflict Gedetecteerd</h3>
                             <p className="text-slate-600 max-w-md mt-2">
                                 Het e-mailadres <strong>{currentCandidate.email || currentCandidate.email2}</strong> is al in gebruik bij een andere kandidaat in Werftools. Omdat e-mailadressen uniek zijn, kan deze overdracht niet worden voltooid.
                             </p>
                             <div className="flex gap-4 mt-8">
                                 <button onClick={handleNext} className="bg-slate-900 border border-slate-700 hover:bg-black text-white px-8 py-3 rounded-xl font-bold uppercase tracking-widest shadow-lg transition-transform hover:scale-105 active:scale-95">
                                     Kandidaat Overslaan & Doorgaan
                                 </button>
                             </div>
                         </div>
                      ) : !mappedProfile ? (
                         <div className="h-full flex flex-col items-center justify-center text-center opacity-50">
                             <FileText size={48} className="text-slate-300 mb-4" />
                             <h3 className="text-lg font-black text-slate-800">Geen profiel gegenereerd</h3>
                             <p className="text-sm text-slate-500 max-w-sm mt-2">Druk op de Froukje AI knop in het midden om de linker data te structureren en vertalen naar het strakke Werftools model.</p>
                         </div>
                      ) : (
                         <div className="animate-fadeIn space-y-6">
                             {/* CLEAN HERO RECORD */}
                             <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100 flex items-start justify-between">
                                 <div>
                                     <h2 className="text-2xl font-black text-slate-900 flex items-center gap-3">
                                         {mappedProfile.firstName} {mappedProfile.lastName}
                                         <span className="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-1 rounded font-bold uppercase tracking-widest">Lead</span>
                                     </h2>
                                     <div className="text-sm font-bold text-[#e51b23] mt-1">{mappedProfile.currentRole || mappedProfile.role || 'Onbekende Rol'}</div>
                                     <div className="flex flex-col gap-1 text-xs text-slate-500 mt-3 border-b border-slate-100 pb-3">
                                         <span>📧 {currentCandidate.email || currentCandidate.email2 || 'Geen email'}</span>
                                         <span>📱 {currentCandidate.phone || currentCandidate.mobile || 'Geen telefoon'}</span>
                                         {currentCandidate.address && (
                                            <span>📍 {currentCandidate.address.address1} {currentCandidate.address.address2 ? currentCandidate.address.address2 : ''}, {currentCandidate.address.zip} {currentCandidate.address.city}</span>
                                         )}
                                     </div>
                                     <p className="text-sm text-slate-500 leading-relaxed mt-3 max-w-xl">{mappedProfile.summary}</p>
                                 </div>
                             </div>

                             {/* CLEAN SKILLS */}
                             {mappedProfile.skills && mappedProfile.skills.length > 0 && (
                                 <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100">
                                     <h3 className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-4">Gedetecteerde Skills</h3>
                                     <div className="flex flex-wrap gap-2">
                                         {mappedProfile.skills.map((s: string, i: number) => (
                                            <span key={i} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold">{s}</span>
                                         ))}
                                     </div>
                                 </div>
                             )}

                             {/* CLEAN TIMELINE */}
                             {mappedProfile.timeline && mappedProfile.timeline.length > 0 && (
                                 <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100">
                                     <h3 className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-4">Strakke Werkervaring</h3>
                                     <div className="space-y-4">
                                         {mappedProfile.timeline.map((t: any, i: number) => (
                                            <div key={i} className="relative pl-6 border-l-2 border-slate-100 pb-2">
                                                <div className="absolute w-3 h-3 bg-slate-200 rounded-full -left-[7px] top-1 border-2 border-white"></div>
                                                <div className="text-xs font-black text-slate-900">{t.title}</div>
                                                <div className="text-xs font-bold text-slate-500 mb-2">{t.company} <span className="mx-2 opacity-30">•</span> {t.period}</div>
                                                {t.description && <div className="text-xs text-slate-500 max-w-xl whitespace-pre-wrap">{t.description}</div>}
                                            </div>
                                         ))}
                                     </div>
                                 </div>
                             )}

                             {/* CLEAN EDUCATIONS */}
                             {mappedProfile.educations && mappedProfile.educations.length > 0 && (
                                 <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100">
                                     <h3 className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-4">Strakke Opleidingen</h3>
                                     <div className="space-y-4">
                                         {mappedProfile.educations.map((ed: any, i: number) => (
                                            <div key={i} className="relative pl-6 border-l-2 border-slate-100 pb-2">
                                                <div className="absolute w-3 h-3 bg-slate-200 rounded-full -left-[7px] top-1 border-2 border-white"></div>
                                                <div className="text-xs font-black text-slate-900">{ed.degree || 'Onbekende Diploma'}</div>
                                                <div className="text-xs font-bold text-slate-500 mb-2">{ed.school} <span className="mx-2 opacity-30">•</span> {ed.period}</div>
                                                {ed.description && <div className="text-xs text-slate-500 max-w-xl whitespace-pre-wrap">{ed.description}</div>}
                                            </div>
                                         ))}
                                     </div>
                                 </div>
                             )}

                             {/* CLEAN COURSES */}
                             {mappedProfile.courses && mappedProfile.courses.length > 0 && (
                                 <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100">
                                     <h3 className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-4">Cursussen & Certificaten</h3>
                                     <div className="space-y-4">
                                         {mappedProfile.courses.map((c: any, i: number) => (
                                            <div key={i} className="relative pl-6 border-l-2 border-slate-100 pb-2">
                                                <div className="absolute w-3 h-3 bg-slate-200 rounded-full -left-[7px] top-1 border-2 border-white"></div>
                                                <div className="text-xs font-black text-slate-900">{c.name}</div>
                                                <div className="text-xs font-bold text-slate-500">{c.institute} <span className="mx-2 opacity-30">•</span> {c.year}</div>
                                            </div>
                                         ))}
                                     </div>
                                 </div>
                             )}

                             {/* CLEAN HOBBIES */}
                             {mappedProfile.hobbies && mappedProfile.hobbies.length > 0 && (
                                 <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100">
                                     <h3 className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-4">Hobby's & Interesses</h3>
                                     <div className="flex flex-wrap gap-2">
                                         {mappedProfile.hobbies.map((h: string, i: number) => (
                                            <span key={i} className="px-3 py-1 bg-slate-50 border border-slate-100 text-slate-600 rounded-lg text-xs font-bold">{h}</span>
                                         ))}
                                     </div>
                                 </div>
                             )}

                             {/* DEEP SYNC TRACKER */}
                             <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-slate-100">
                                 <h3 className="text-[10px] uppercase font-black tracking-widest text-emerald-500 mb-4 flex items-center gap-2">
                                     <RefreshCw size={12} className="animate-spin-slow" /> Wordt Diep-Gesynchroniseerd:
                                 </h3>
                                 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                     <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 flex flex-col items-center justify-center text-center">
                                         <div className="text-2xl font-black text-slate-800">{currentCandidateFiles.length}</div>
                                         <div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mt-1">Bestanden</div>
                                     </div>
                                     <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 flex flex-col items-center justify-center text-center">
                                         <div className="text-2xl font-black text-slate-800">{currentCandidateNotes.length}</div>
                                         <div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mt-1">Notities</div>
                                     </div>
                                     <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 flex flex-col items-center justify-center text-center">
                                         <div className="text-2xl font-black text-slate-800">{currentCandidateSubmissions.length}</div>
                                         <div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mt-1">Vacature Koppelingen</div>
                                     </div>
                                 </div>
                             </div>

                         </div>
                      )}
                  </div>
                  <label className="flex items-center gap-2 mt-4 text-xs text-slate-500 font-bold bg-slate-50 p-3 rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-100 transition">
                      <input type="checkbox" checked={useFastSync} onChange={(e) => setUseFastSync(e.target.checked)} className="rounded text-emerald-500 w-4 h-4 cursor-pointer" />
                      Snelle Sync (Sla Volledige AI Analyse & Documenten Over en importeer direct)
                  </label>
               </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default BullhornInteractiveMapper;
